<template>
    <section class="student-assignment">
        <div class="container student-assignment__container" :class="{ 'is-embedded': embedded }">
            <StudentSidebar v-if="!embedded" :student="student" />

            <main class="student-assignment__main">
                <header class="student-assignment__topbar">
                    <div>
                        <span class="student-assignment__eyebrow">Assignment</span>
                        <h1>{{ assignment.title }}</h1>
                        <p>{{ assignment.courseTitle }}<span v-if="assignment.chapterTitle"> / {{ assignment.chapterTitle }}</span></p>
                    </div>

                    <a :href="assignment.backUrl || '/student/assignments'" class="student-assignment__back">
                        <i class="ri-arrow-left-line"></i>
                        Back
                    </a>
                </header>

                <section class="student-assignment__grid">
                    <article class="student-assignment__panel">
                        <span class="student-assignment__eyebrow">Brief</span>
                        <h2>Assignment Details</h2>
                        <p>{{ assignment.description || 'No description provided.' }}</p>

                        <dl>
                            <div class="student-assignment__deadline-card">
                                <dt>Deadline</dt>
                                <dd>{{ assignment.deadlineLabel }}</dd>
                            </div>
                            <div>
                                <dt>Total Mark</dt>
                                <dd>{{ assignment.totalMark || 0 }}</dd>
                            </div>
                            <div>
                                <dt>Pass Mark</dt>
                                <dd>{{ assignment.passMark || 0 }}</dd>
                            </div>
                            <div>
                                <dt>Obtained Mark</dt>
                                <dd>{{ marksText(assignment.marksObtained ?? assignment.submission?.marksObtained) }}</dd>
                            </div>
                        </dl>

                        <a v-if="assignment.attachmentPath" :href="assignment.attachmentPath" target="_blank" rel="noopener" class="student-assignment__download">
                            <i class="ri-attachment-2"></i>
                            Download Attachment
                        </a>

                        <div v-if="showSubmissionUnderBrief" class="student-assignment__submitted student-assignment__submitted--brief">
                            <div class="student-assignment__file-group-head">
                                <strong>Current submission</strong>
                            </div>

                            <div class="student-assignment__file-list">
                                <a
                                    v-for="file in submissionFiles"
                                    :key="file.id"
                                    :href="file.mediaPath"
                                    target="_blank"
                                    rel="noopener"
                                    class="student-assignment__submitted-file"
                                    :class="{ 'is-main': file.kind === 'main' && isSubmittedStatus }"
                                >
                                    <div class="student-assignment__file-icon" :class="{ 'is-primary': file.kind === 'main' }">
                                        <i :class="fileIconClass(file.name)"></i>
                                    </div>
                                    <div class="student-assignment__file-copy">
                                        <div class="student-assignment__file-heading">
                                            <strong class="student-assignment__file-title">
                                                <span class="student-assignment__file-name">{{ file.name }}</span>
                                            </strong>
                                            <span v-if="file.kind === 'main'" class="student-assignment__file-badge">Main file</span>
                                        </div>
                                        <p>{{ file.kind === 'main' ? 'Primary submission file' : 'Supporting attachment' }}</p>
                                    </div>
                                    <span class="student-assignment__file-link-icon" aria-hidden="true">
                                        <i class="ri-external-link-line"></i>
                                    </span>
                                </a>
                            </div>
                        </div>
                    </article>

                    <form class="student-assignment__panel" @submit.prevent="submitAssignment">
                        <div class="student-assignment__form-head">
                            <div>
                                <span class="student-assignment__eyebrow">Submit work</span>
                                <h2>{{ formTitle }}</h2>
                            </div>
                            <span v-if="assignment.submission && !assignment.isApproved" class="student-assignment__status">
                                {{ assignment.submission.statusLabel || 'Pending' }}
                            </span>
                        </div>

                        <div v-if="assignment.isApproved" class="student-assignment__approved">
                            <i class="ri-checkbox-circle-line"></i>
                            This assignment has been approved. Updates are now locked.
                        </div>

                        <div v-else-if="assignment.isCourseExpired" class="student-assignment__closed">
                            <i class="ri-lock-line"></i>
                            This course access has expired.
                        </div>

                        <div v-else-if="assignment.isExpired" class="student-assignment__closed">
                            <i class="ri-lock-line"></i>
                            Submission deadline has expired.
                        </div>

                        <template v-else>
                            <section class="student-assignment__upload-stack">
                                <div class="student-assignment__upload-head">
                                    <div>
                                        <span>Assignment Files</span>
                                        <p>Upload one or more files. We will keep the submission set together.</p>
                                    </div>
                                    <strong>{{ pendingFiles.length || submissionFiles.length }} file{{ pendingFiles.length + submissionFiles.length === 1 ? '' : 's' }}</strong>
                                </div>

                                <input
                                    ref="fileInput"
                                    class="student-assignment__input-hidden"
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                    @change="handleFileChange"
                                />

                                <button type="button" class="student-assignment__dropzone" @click="openFilePicker">
                                    <span class="student-assignment__dropzone-icon">
                                        <i class="ri-upload-cloud-2-line"></i>
                                    </span>
                                    <strong>{{ pendingFiles.length ? 'Add more files to this submission' : 'Choose files for your assignment' }}</strong>
                                    <small>PDF, image, document, spreadsheet, or text files up to 20 MB each</small>
                                </button>

                                <small v-if="errors.media_file" class="student-assignment__error">{{ errors.media_file[0] }}</small>
                                <small v-else-if="errors.attachments" class="student-assignment__error">{{ errors.attachments[0] }}</small>

                                <div v-if="pendingFiles.length" class="student-assignment__file-group">
                                    <div class="student-assignment__file-group-head">
                                        <strong>Ready to upload</strong>
                                        <button type="button" class="student-assignment__text-action" @click="clearPendingFiles">Clear</button>
                                    </div>

                                    <div class="student-assignment__file-list">
                                        <article v-for="(file, index) in pendingFiles" :key="pendingFileKey(file, index)" class="student-assignment__file-card">
                                            <div class="student-assignment__file-icon" :class="{ 'is-primary': index === 0 }">
                                                <i :class="fileIconClass(file.name)"></i>
                                            </div>
                                            <div class="student-assignment__file-copy">
                                                <strong class="student-assignment__file-title">
                                                    <span class="student-assignment__file-name">{{ file.name }}</span>
                                                </strong>
                                                <p>{{ formatFileSize(file.size) }}</p>
                                            </div>
                                            <button type="button" class="student-assignment__file-remove" @click="removePendingFile(index)">
                                                <i class="ri-delete-bin-6-line"></i>
                                            </button>
                                        </article>
                                    </div>
                                </div>
                            </section>

                            <label class="student-assignment__field">
                                <span>Remarks</span>
                                <textarea v-model="remarks" rows="5" placeholder="Write optional remarks..."></textarea>
                                <small v-if="errors.remarks">{{ errors.remarks[0] }}</small>
                            </label>

                            <button type="submit" class="student-assignment__submit-button" :disabled="isSubmitting || !canSubmitAssignment">
                                <i class="ri-upload-cloud-2-line"></i>
                                {{ isSubmitting ? 'Submitting...' : submitButtonLabel }}
                            </button>
                        </template>

                        <div v-if="showSubmissionOnWorkPanel" class="student-assignment__submitted">
                            <div class="student-assignment__file-group-head">
                                <strong>Current submission</strong>
                            </div>

                            <div class="student-assignment__file-list">
                                <a
                                    v-for="file in submissionFiles"
                                    :key="file.id"
                                    :href="file.mediaPath"
                                    target="_blank"
                                    rel="noopener"
                                    class="student-assignment__submitted-file"
                                >
                                    <div class="student-assignment__file-icon" :class="{ 'is-primary': file.kind === 'main' }">
                                        <i :class="fileIconClass(file.name)"></i>
                                    </div>
                                    <div class="student-assignment__file-copy">
                                        <div class="student-assignment__file-heading">
                                            <strong class="student-assignment__file-title">
                                                <span class="student-assignment__file-name">{{ file.name }}</span>
                                            </strong>
                                            <span v-if="file.kind === 'main'" class="student-assignment__file-badge">Main file</span>
                                        </div>
                                        <p>{{ file.kind === 'main' ? 'Primary submission file' : 'Supporting attachment' }}</p>
                                    </div>
                                    <span class="student-assignment__file-link-icon" aria-hidden="true">
                                        <i class="ri-external-link-line"></i>
                                    </span>
                                </a>
                            </div>
                        </div>
                    </form>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentAssignmentSubmitComponent')
    || document.getElementById('employeeInternalAssignmentSubmitComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const embedded = root?.dataset.embedded === 'true';
const assignment = reactive(parseJson(root?.dataset.assignment, {}));
const remarks = ref(assignment.submission?.remarks || '');
const fileInput = ref(null);
const pendingFiles = ref([]);
const errors = ref({});
const isSubmitting = ref(false);
const canSubmitAssignment = computed(() => assignment.canSubmit !== false && !assignment.isCourseExpired && !assignment.isExpired && !assignment.isApproved);
const submissionFiles = computed(() => {
    const files = [];

    if (assignment.submission?.mediaPath) {
        files.push({
            id: `main-${assignment.submission.id}`,
            mediaPath: assignment.submission.mediaPath,
            name: assignment.submission.mainFileName || 'Main file',
            kind: 'main',
        });
    }

    return files.concat(
        (assignment.submission?.attachments || []).map((file) => ({
            id: `attachment-${file.id}`,
            mediaPath: file.mediaPath,
            name: file.name || 'Attachment',
            kind: 'attachment',
        })),
    );
});
const showSubmissionUnderBrief = computed(() => submissionFiles.value.length > 0 && Boolean(assignment.submission) && !assignment.isApproved);
const showSubmissionOnWorkPanel = computed(() => submissionFiles.value.length > 0 && !showSubmissionUnderBrief.value);
const isSubmittedStatus = computed(() => (assignment.submission?.statusLabel || '').toLowerCase() === 'submitted' || assignment.submission?.status === 'pending');
const formTitle = computed(() => {
    if (assignment.isApproved) return 'Submission Approved';
    return assignment.submission ? 'Update Submission' : 'New Submission';
});
const submitButtonLabel = computed(() => {
    if (assignment.submission?.status === 'rejected') return 'Resubmit Assignment';
    return assignment.submission ? 'Update Assignment' : 'Submit Assignment';
});

const notify = (message, type = 'success') => {
    if (typeof window.$toast === 'function') {
        window.$toast(message, type);
    }
};

const openFilePicker = () => {
    fileInput.value?.click();
};

const pendingFileKey = (file, index) => `${file.name}-${file.size}-${file.lastModified}-${index}`;

const handleFileChange = (event) => {
    const incomingFiles = Array.from(event.target.files || []);

    if (!incomingFiles.length) {
        return;
    }

    const seen = new Set(pendingFiles.value.map((file) => pendingFileKey(file, 0)));

    incomingFiles.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}-0`;

        if (!seen.has(key)) {
            pendingFiles.value.push(file);
            seen.add(key);
        }
    });

    event.target.value = '';
};

const removePendingFile = (index) => {
    pendingFiles.value.splice(index, 1);
};

const clearPendingFiles = () => {
    pendingFiles.value = [];

    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const submitAssignment = async () => {
    if (!canSubmitAssignment.value) {
        notify('This assignment cannot be updated right now.', 'warning');
        return;
    }

    isSubmitting.value = true;
    errors.value = {};

    const formData = new FormData();
    formData.append('remarks', remarks.value || '');

    if (pendingFiles.value[0]) {
        formData.append('media_file', pendingFiles.value[0]);
    }

    pendingFiles.value.slice(1).forEach((file) => {
        formData.append('attachments[]', file);
    });

    try {
        const { data } = await window.axios.post(assignment.submitUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (data.status) {
            assignment.submission = data.data;
            clearPendingFiles();
            notify(data.message || 'Assignment submitted successfully.', data.type || 'success');
            setTimeout(() => {
                window.location.href = assignment.backUrl || '/student/assignments';
            }, 800);
        }
    } catch (error) {
        if (error.response?.status === 422) {
            errors.value = error.response.data.errors || {};
            notify(error.response.data.message || 'Please fix validation errors.', 'error');
        } else {
            notify(error.response?.data?.message || 'Unable to submit right now.', 'error');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const marksText = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const number = Number(value);

    return Number.isFinite(number) ? number.toFixed(2) : '-';
};

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '0 KB';
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fileIconClass = (filename = '') => {
    const extension = filename.split('.').pop()?.toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        return 'ri-image-2-line';
    }

    if (extension === 'pdf') {
        return 'ri-file-pdf-line';
    }

    if (['doc', 'docx', 'txt'].includes(extension)) {
        return 'ri-file-text-line';
    }

    if (['xls', 'xlsx'].includes(extension)) {
        return 'ri-file-chart-line';
    }

    if (['ppt', 'pptx'].includes(extension)) {
        return 'ri-file-list-3-line';
    }

    return 'ri-file-line';
};
</script>

<style scoped>
.student-assignment {
    background: linear-gradient(180deg, #f5f8fc 0%, #eef3f7 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-assignment__container.is-embedded {
    grid-template-columns: minmax(0, 1fr);
    max-width: none;
    padding-inline: 0;
}

.student-assignment__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-assignment__main {
    min-width: 0;
}

.student-assignment__topbar,
.student-assignment__panel {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-assignment__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)), #ffffff;
    border-color: #dcecff;
}

.student-assignment__topbar h1,
.student-assignment__panel h2 {
    margin: 0;
    color: #111827;
    font-weight: 900;
}

.student-assignment__topbar h1 {
    font-size: 24px;
}

.student-assignment__topbar p,
.student-assignment__panel p {
    margin: 7px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.65;
    font-weight: 700;
}

.student-assignment__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
}

.student-assignment__back,
.student-assignment__download,
.student-assignment__submitted a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 900;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-assignment__back:hover,
.student-assignment__download:hover,
.student-assignment__submitted a:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-assignment__grid {
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1fr);
    gap: 18px;
    align-items: start;
}

.student-assignment__panel {
    padding: 22px;
}

.student-assignment__panel dl {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin: 18px 0;
}

.student-assignment__panel dl div {
    padding: 12px;
    border-radius: 8px;
    background: #f8fafc;
}

.student-assignment__panel dl .student-assignment__deadline-card {
    border: 1px solid #fecaca;
    background: linear-gradient(180deg, #fff7f7 0%, #fef2f2 100%);
}

.student-assignment__deadline-card dt,
.student-assignment__deadline-card dd {
    color: #dc2626;
}

.student-assignment__panel dt,
.student-assignment__panel dd {
    margin: 0;
}

.student-assignment__panel dt {
    color: #64748b;
    font-size: 11px;
    font-weight: 900;
}

.student-assignment__panel dd {
    margin-top: 4px;
    color: #111827;
    font-size: 13px;
    font-weight: 900;
}

.student-assignment__form-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.student-assignment__status {
    display: inline-flex;
    min-height: 28px;
    align-items: center;
    padding: 5px 10px;
    border-radius: 999px;
    background: #fff7ed;
    color: #f97316;
    font-size: 12px;
    font-weight: 900;
}

.student-assignment__field {
    display: grid;
    gap: 7px;
    margin-bottom: 14px;
}

.student-assignment__field span {
    color: #475569;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
}

.student-assignment__field input,
.student-assignment__field textarea {
    width: 100%;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #111827;
    font-size: 14px;
    outline: 0;
    padding: 11px 12px;
}

.student-assignment__field small {
    color: #dc2626;
    font-size: 12px;
    font-weight: 800;
}

.student-assignment__upload-stack {
    display: grid;
    gap: 14px;
    margin-bottom: 18px;
}

.student-assignment__upload-head,
.student-assignment__file-group-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.student-assignment__upload-head span,
.student-assignment__file-group-head strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
}

.student-assignment__upload-head p,
.student-assignment__file-group-head span {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.student-assignment__upload-head strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 7px 12px;
    border-radius: 999px;
    background: #eff6ff;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
}

.student-assignment__input-hidden {
    display: none;
}

.student-assignment__dropzone {
    display: grid;
    justify-items: center;
    gap: 8px;
    width: 100%;
    padding: 24px 18px;
    border: 1px dashed #b7cff3;
    border-radius: 8px;
    background: linear-gradient(180deg, #f8fbff 0%, #f1f7ff 100%);
    color: #111827;
    text-align: center;
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.student-assignment__dropzone:hover {
    border-color: #0d6efd;
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(13, 110, 253, 0.08);
}

.student-assignment__dropzone strong {
    font-size: 16px;
    font-weight: 900;
}

.student-assignment__dropzone small {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.student-assignment__dropzone-icon,
.student-assignment__file-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 19px;
}

.student-assignment__dropzone-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0d6efd 0%, #2f7d5a 100%);
    color: #ffffff;
    font-size: 22px;
}

.student-assignment__file-icon.is-primary {
    background: linear-gradient(135deg, #0d6efd 0%, #2f7d5a 100%);
    color: #ffffff;
}

.student-assignment__file-group {
    display: grid;
    gap: 12px;
}

.student-assignment__file-list {
    display: grid;
    gap: 10px;
}

.student-assignment__file-card,
.student-assignment__submitted-file {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 28px;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    overflow: hidden;
}

.student-assignment__file-icon {
    justify-self: center;
}

.student-assignment__file-link-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    min-width: 28px;
    color: #2563eb;
    font-size: 16px;
    justify-self: center;
}

.student-assignment__submitted-file {
    color: inherit;
    text-decoration: none;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.student-assignment__submitted-file.is-main {
    grid-template-columns: 42px minmax(0, 1fr) 32px;
    gap: 8px;
    padding-left: 10px;
    padding-right: 8px;
}

.student-assignment__submitted-file.is-main .student-assignment__file-icon {
    justify-self: start;
}

.student-assignment__submitted-file.is-main .student-assignment__file-copy {
    display: grid;
    align-content: center;
    min-height: 42px;
}

.student-assignment__submitted-file.is-main .student-assignment__file-link-icon {
    width: 32px;
    min-width: 32px;
    justify-self: end;
}

.student-assignment__submitted-file:hover {
    border-color: #bfd5f6;
    background: #f4f8ff;
    transform: translateY(-1px);
}

.student-assignment__file-copy {
    min-width: 0;
}

.student-assignment__file-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    min-width: 0;
}

.student-assignment__file-title {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    color: #111827;
    font-size: 14px;
    font-weight: 900;
    line-height: 1.5;
}

.student-assignment__file-name {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
}

.student-assignment__file-badge,
.student-assignment__file-copy strong span {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #fff7ed;
    color: #ea580c;
    font-size: 11px;
    font-weight: 900;
    flex-shrink: 0;
    max-width: 100%;
}

.student-assignment__file-copy p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    word-break: break-word;
}

.student-assignment__file-remove,
.student-assignment__text-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    color: #dc2626;
    transition: color 0.2s ease, background 0.2s ease;
}

.student-assignment__file-remove {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #fff1f2;
    font-size: 16px;
}

.student-assignment__file-remove:hover {
    background: #ffe4e6;
    color: #b91c1c;
}

.student-assignment__text-action {
    padding: 0;
    background: transparent;
    font-size: 12px;
    font-weight: 900;
}

.student-assignment__text-action:hover {
    color: #b91c1c;
}

.student-assignment__error {
    color: #dc2626;
    font-size: 12px;
    font-weight: 800;
}

.student-assignment__submit-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    width: 100%;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, #0d6efd 0%, #2f7d5a 100%);
    color: #ffffff;
    font-size: 14px;
    font-weight: 900;
}

.student-assignment__closed,
.student-assignment__approved {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px;
    border-radius: 8px;
    background: #fef2f2;
    color: #dc2626;
    font-size: 13px;
    font-weight: 900;
}

.student-assignment__approved {
    background: #ecfdf5;
    color: #16a34a;
}

.student-assignment__submitted {
    display: grid;
    gap: 12px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #eef2f7;
}

.student-assignment__submitted--brief {
    margin-top: 20px;
}

.student-assignment__submitted strong {
    color: #111827;
    font-size: 13px;
    font-weight: 900;
}

@media (max-width: 991px) {
    .student-assignment__container,
    .student-assignment__grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575px) {
    .student-assignment__container {
        padding: 0 10px;
    }

    .student-assignment__topbar,
    .student-assignment__upload-head,
    .student-assignment__file-group-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-assignment__form-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-assignment__back {
        width: 100%;
    }

    .student-assignment__panel dl {
        grid-template-columns: 1fr;
    }

    .student-assignment__file-heading {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .student-assignment__file-card,
    .student-assignment__submitted-file {
        gap: 10px;
        padding: 10px;
    }
}
</style>
